// TypeScript
import { equals } from "ramda";
import { useCallback, useMemo, useReducer } from "react";

export type FocusPath = (number | null)[];

type Action =
  | { type: "setAt"; index: number; value: number | null }
  | { type: "clearFrom"; index: number } // sets all indices > index to null; does not change items < index
  | { type: "reset"; next?: FocusPath };

interface State {
  path: FocusPath;
}

const ensureLength = (arr: FocusPath, len: number): FocusPath => {
  if (arr.length >= len) return arr.slice(); // copy
  return [...arr, ...Array<null>(len - arr.length).fill(null)];
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "setAt": {
      // Ensure we can set at index
      const next = ensureLength(state.path, action.index + 1);
      next[action.index] = action.value;
      // All items after index must become null
      for (let i = action.index + 1; i < next.length; i++) {
        next[i] = null;
      }
      return { path: next };
    }

    case "clearFrom": {
      if (state.path.length === 0) return state;
      const next = state.path.slice();
      for (let i = action.index + 1; i < next.length; i++) {
        next[i] = null;
      }
      return { path: next };
    }

    case "reset": {
      const nextPath = action.next ? action.next.slice() : [];
      if (equals(nextPath, state.path)) {
        return state;
      }
      return { path: nextPath };
    }

    default:
      return state;
  }
};

interface FocusPathContextValue {
  path: FocusPath;
  setAt: (index: number, value: number | null) => void;
  clearFrom: (index: number) => void;
  reset: (next?: FocusPath) => void;
}

export interface FocusPathProviderProps {
  // Optional initial path (e.g., [2, 1, null])
  initialPath?: FocusPath;
}

export const useFocusPath = (props: FocusPathProviderProps): FocusPathContextValue => {
  const [state, dispatch] = useReducer(reducer, { path: props.initialPath?.slice() ?? [] });

  const setAt = useCallback((index: number, value: number | null) => {
    dispatch({ type: "setAt", index, value });
  }, []);

  const clearFrom = useCallback((index: number) => {
    dispatch({ type: "clearFrom", index });
  }, []);

  const reset = useCallback((next?: FocusPath) => {
    dispatch({ type: "reset", next });
  }, []);

  return useMemo<FocusPathContextValue>(
    () => ({
      path: state.path,
      setAt,
      clearFrom,
      reset,
    }),
    [state.path, setAt, clearFrom, reset]
  );
};
